import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, api, TransportError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button, TextField } from '../ui/index.js';

type SearchResult = Awaited<ReturnType<typeof api.get<'/api/v1/search'>>>;

/**
 * Finding a message.
 *
 * Searching on submit rather than on every keystroke: each search embeds the query on a model
 * server, and a search-as-you-type box would send five requests for a word somebody is still
 * spelling.
 *
 * Every hit says which engine found it. "Matched your words" and "matched the meaning" are
 * different claims, and a reader who cannot tell them apart will eventually distrust both.
 */
export function SearchScreen(): ReactNode {
  const { t, translator } = useI18n();
  const { activeWorkspace } = useAuth();

  const [query, setQuery] = useState('');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [searched, setSearched] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const tenantId = activeWorkspace?.id;

  const run = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const text = query.trim();
      if (tenantId === undefined || text === '') return;
      setBusy(true);
      setFailure(null);
      try {
        const found = await api.get('/api/v1/search', { q: text }, { tenantId });
        setResult(found);
        setSearched(text);
      } catch (error) {
        setResult(null);
        setFailure(
          error instanceof ApiError
            ? error.code
            : error instanceof TransportError
              ? 'UNREACHABLE'
              : 'INTERNAL_ERROR',
        );
      } finally {
        setBusy(false);
      }
    },
    [tenantId, query],
  );

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('search.title')}</h1>
      </header>

      <section className="card">
        <form onSubmit={(event) => void run(event)}>
          <TextField
            label={t('search.label')}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={t('search.placeholder')}
            hint={t('search.hint')}
          />
          <div className="mailbox-form__actions">
            <Button variant="primary" type="submit" disabled={busy || query.trim() === ''}>
              {t('search.submit')}
            </Button>
          </div>
        </form>
      </section>

      {failure !== null && (
        <Alert tone="critical" title={t('search.error.title')}>
          <p className="empty__body">{t('search.error.body')}</p>
        </Alert>
      )}

      {result !== null && result.keywordOnly && (
        /* Said rather than hidden: a reader comparing two searches deserves to know one of the
           two engines was unavailable for this one. */
        <p className="mail-message__blocked">{t('search.keywordOnly')}</p>
      )}

      {result !== null && (
        <section className="card">
          <h2 className="card__title">
            {result.hits.length === 0
              ? t('search.noResults', { query: searched })
              : t('search.results', { count: result.hits.length, query: searched })}
          </h2>
          {result.hits.length > 0 && (
            <ul className="today-list">
              {result.hits.map((hit) => (
                <li className="today-list__item" key={hit.messageId}>
                  <span className="badge">{t(`search.matched.${hit.matchedBy}`)}</span>
                  <span className="today-list__subject">
                    {hit.subject === '' ? t('mail.noSubject') : hit.subject}
                  </span>
                  <span className="today-list__meta">
                    {[
                      hit.fromName ?? hit.from,
                      translator.formatDate(new Date(hit.receivedAt)),
                      hit.hasAttachments ? t('search.hasAttachments') : null,
                    ]
                      .filter((part) => part !== null && part !== '')
                      .join(' · ')}
                  </span>
                  {hit.snippet !== '' && <p className="thread__snippet">{hit.snippet}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}
